/**
 * Email Service
 * AIDLC Administrative Frontend
 *
 * API calls for email management (inbox, signature, OneDrive, integrations).
 */

import apiClient from './api';
import type {
  InboxMessage,
  InboxListResponse,
  EmailSignatureTemplate,
  EmailIntegrationConfig,
  OneDriveItem,
  DraftReplyRequest,
  DraftReplyResponse,
} from '../types/email';

const BASE = '/email';

// ─── Inbox ────────────────────────────────────────────────────────

export async function getInboxMessages(
  userEmail: string,
  top = 25,
  skip = 0
): Promise<InboxListResponse> {
  const response = await apiClient.get<InboxListResponse>(
    `${BASE}/inbox/${encodeURIComponent(userEmail)}/messages`,
    { params: { top, skip } }
  );
  return response.data;
}

export async function getInboxMessage(
  userEmail: string,
  messageId: string
): Promise<InboxMessage> {
  const response = await apiClient.get<InboxMessage>(
    `${BASE}/inbox/${encodeURIComponent(userEmail)}/messages/${messageId}`
  );
  return response.data;
}

export async function markMessageRead(
  userEmail: string,
  messageId: string
): Promise<void> {
  await apiClient.post(
    `${BASE}/inbox/${encodeURIComponent(userEmail)}/messages/${messageId}/read`
  );
}

export async function replyToMessage(
  userEmail: string,
  messageId: string,
  body: string,
  attachmentItemIds?: string[]
): Promise<void> {
  await apiClient.post(
    `${BASE}/inbox/${encodeURIComponent(userEmail)}/messages/${messageId}/reply`,
    { body, attachmentItemIds }
  );
}

export async function draftReply(
  userEmail: string,
  messageId: string,
  data: DraftReplyRequest
): Promise<DraftReplyResponse> {
  const response = await apiClient.post<DraftReplyResponse>(
    `${BASE}/inbox/${encodeURIComponent(userEmail)}/messages/${messageId}/draft-reply`,
    data
  );
  return response.data;
}

// ─── Signature ────────────────────────────────────────────────────

export async function getSignatureTemplate(mailbox: string): Promise<EmailSignatureTemplate> {
  const response = await apiClient.get<EmailSignatureTemplate>(`${BASE}/signature`, {
    params: { mailbox },
  });
  return response.data;
}

export async function updateSignatureTemplate(
  mailbox: string,
  template: Partial<EmailSignatureTemplate>
): Promise<EmailSignatureTemplate> {
  const response = await apiClient.put<EmailSignatureTemplate>(`${BASE}/signature`, template, {
    params: { mailbox },
  });
  return response.data;
}

export async function getSignaturePreview(mailbox: string): Promise<string> {
  const response = await apiClient.get<{ html: string }>(`${BASE}/signature/preview`, {
    params: { mailbox },
  });
  return response.data.html;
}

// ─── Integrations ─────────────────────────────────────────────────

export async function getIntegrations(): Promise<EmailIntegrationConfig[]> {
  const response = await apiClient.get<EmailIntegrationConfig[]>(`${BASE}/integrations`);
  return response.data;
}

export async function addIntegration(
  data: { userEmail: string; displayName: string }
): Promise<EmailIntegrationConfig> {
  const response = await apiClient.post<EmailIntegrationConfig>(`${BASE}/integrations`, data);
  return response.data;
}

export async function removeIntegration(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/integrations/${id}`);
}

export async function updateIntegration(
  id: string,
  updates: Partial<Pick<EmailIntegrationConfig, 'displayName' | 'protected' | 'features'>>
): Promise<EmailIntegrationConfig> {
  const response = await apiClient.patch<EmailIntegrationConfig>(`${BASE}/integrations/${id}`, updates);
  return response.data;
}

// ─── OneDrive ─────────────────────────────────────────────────────

export async function getOneDriveFiles(
  userEmail: string,
  folderId?: string
): Promise<OneDriveItem[]> {
  const endpoint = folderId
    ? `${BASE}/onedrive/${encodeURIComponent(userEmail)}/files/${folderId}`
    : `${BASE}/onedrive/${encodeURIComponent(userEmail)}/files`;
  const response = await apiClient.get<OneDriveItem[]>(endpoint);
  return response.data;
}

export async function getOneDriveFileDownloadUrl(
  userEmail: string,
  itemId: string
): Promise<string> {
  const response = await apiClient.get<{ url: string }>(
    `${BASE}/onedrive/${encodeURIComponent(userEmail)}/files/${itemId}/content`
  );
  return response.data.url;
}
